import { useEffect, useState } from "react";
import { FileDown, Paperclip, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/i18n";
import { Tag } from "@/components/app/kit";

export const STATUS_FR = {
  requested: "Demandé", confirmed: "Confirmé", arrived: "Arrivé", in_consultation: "En consultation", done: "Terminé", cancelled: "Annulé",
  no_show: "Absent", draft: "Brouillon", finalized: "Finalisée", validated: "Validé", sampled: "Prélevé", in_progress: "En cours",
  result_available: "Résultat disponible", transmitted: "Transmis",
};
const TONE = { done: "green", finalized: "green", validated: "green", transmitted: "indigo", cancelled: "gray", no_show: "red", draft: "amber",
  requested: "amber", confirmed: "sky", arrived: "indigo", in_consultation: "indigo", result_available: "amber", sampled: "sky", in_progress: "sky" };
export const MedStatus = ({ s }) => <Tag tone={TONE[s] || "gray"} data-testid={`status-${s}`}>{STATUS_FR[s] || s}</Tag>;

export const useStaff = () => {
  const [staff, setStaff] = useState([]);
  const { can } = useAuth();
  useEffect(() => {
    if (can("staff:read")) api.get("/t/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return staff;
};
export const staffName = (s) => (s ? `${s.first_name} ${s.last_name}` : "");

export const StaffSelect = ({ value, onChange, staff, testid, onlyDoctors, placeholder = "Praticien" }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{staff.filter((s) => s.status === "active" && (!onlyDoctors || s.function === "medecin")).map((s) => <SelectItem key={s.id} value={s.id}>{staffName(s)} {s.specialty && `· ${s.specialty}`}</SelectItem>)}</SelectContent>
  </Select>
);

export const PatientPicker = ({ value, onChange, testid = "patient-picker" }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (value && !label) api.get(`/t/patients/${value}`).then((r) => setLabel(`${r.data.last_name.toUpperCase()} ${r.data.first_name} (${r.data.file_number})`)).catch(() => {});
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (q.length < 2) return setItems([]);
    const t = setTimeout(() => api.get("/t/patients", { params: { search: q, page_size: 8 } }).then((r) => setItems(r.data.items)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Input value={label || q} onChange={(e) => { setLabel(""); onChange(null); setQ(e.target.value); }} placeholder="Rechercher un patient (nom, n°, tél.)" className="bg-white" data-testid={testid} />
      {items.length > 0 && !label && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {items.map((p) => (
            <button type="button" key={p.id} className="block w-full text-left px-3 py-2 text-sm hover:bg-sky-50" data-testid={`${testid}-option-${p.file_number}`}
              onClick={() => { onChange(p.id); setLabel(`${p.last_name.toUpperCase()} ${p.first_name} (${p.file_number})`); setItems([]); }}>
              {p.last_name.toUpperCase()} {p.first_name} <span className="text-xs text-slate-400">{p.file_number} · {p.birth_date || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const PdfButton = ({ kind, id, perm }) => {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!can(perm)) return null;
  const open = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/t/pdf/${kind}/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      toast.error("Export PDF impossible");
    } finally {
      setBusy(false);
    }
  };
  return <Button size="sm" variant="outline" onClick={open} disabled={busy} data-testid={`pdf-${kind}-${id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}<span className="ml-2">PDF</span></Button>;
};

export const Attachments = ({ entityType, entityId, canWrite }) => {
  const [files, setFiles] = useState([]);
  const load = () => api.get("/t/files", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = (file) => {
    if (!file) return;
    if (file.size > 3e6) return toast.error("3 Mo maximum");
    const r = new FileReader();
    r.onload = async () => {
      try {
        await api.post("/t/files", { entity_type: entityType, entity_id: entityId, name: file.name, data_url: r.result });
        toast.success("Pièce jointe ajoutée");
        load();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    r.readAsDataURL(file);
  };
  const download = async (f) => {
    const r = await api.get(`/t/files/${f.id}/download`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = f.name;
    a.click();
  };
  return (
    <div className="space-y-2" data-testid="attachments">
      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />Pièces jointes</p>
      {files.map((f) => <button key={f.id} onClick={() => download(f)} className="block text-sm text-sky-700 hover:underline">{f.name} <span className="text-xs text-slate-400">· {Math.round(f.size / 1024)} Ko · {fmtDateTime(f.created_at)}</span></button>)}
      {!files.length && <p className="text-xs text-slate-400">Aucune pièce jointe.</p>}
      {canWrite && <label className="inline-flex items-center gap-2 text-sm text-sky-700 cursor-pointer"><Upload className="h-4 w-4" />Ajouter un fichier<input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="attachment-input" /></label>}
    </div>
  );
};

export const today = () => new Date().toISOString().slice(0, 10);
export const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
